// 🔴 SECURITY (Wave 1B, 2026-04-29) — eval 사진 thumb 인증 다운로드 + blob URL Map 관리
// 정적 마운트 (/uploads/eval/*) 제거 대비 — 인증 endpoint /api/eval/orders/:id/photos/:filename/thumb 사용.
// utils/evalFileAccess.js 미들웨어와 짝 (현재 routes/eval-photos.js 의 canAccessEvalOrder 가드 동작 중).
//
// 사용:
//   var thumbBlobs = useEvalThumbBlobs(photos);
//   <img src={thumbBlobs[p.url] || ''} />   // blob URL 도착 전엔 빈 src
//
// 입력 photos: [{ url: '/uploads/eval/<orderId>/<filename>', ... }]
// 출력: { [photo.url]: blobUrl } map. 동영상은 키 미포함 (호출자가 p.url 직접 사용).
//
// 주의:
//   - photos 변경 (url 시그니처) 시 이전 blob 전부 revoke + 새 fetch
//   - unmount 시 cleanup 으로 전체 revoke
//   - thumb 401/403/404 시 해당 키 미포함 — 호출자 fallback (빈 src 또는 placeholder)
//   - 4-chunk throttle — 최대 44장 검차 사진 처리 시 동시 connection 폭주 방지
//   - 50장 hard cap — 100장 오더 진입 시 메모리 폭증 방지. cap 초과 시 console.warn + 처음 50장만 로드.
//     Wave 1D lazy load (intersection observer / virtualized grid) 필요.

import { useState, useEffect } from 'react';
import { API, authFetch } from './api';

var STATIC_RE = /^\/uploads\/eval\/(\d+)\/(.+)$/;
var VIDEO_RE = /\.(mp4|mov|avi|webm|3gp)$/i;
var CHUNK_SIZE = 4;
var MAX_THUMB_LOAD = 50;

function chunkArray(arr, size) {
  var result = [];
  for (var i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

function useEvalThumbBlobs(photos) {
  var ret = useState({});
  var blobs = ret[0];
  var setBlobs = ret[1];

  // dep 안정화: url 시그니처. 부모가 매번 새 배열 만들어도 url 동일하면 재실행 X
  var sig = (photos || []).map(function(p) { return p && p.url ? p.url : ''; }).join('|');

  useEffect(function() {
    var list = (photos || []).filter(function(p) {
      if (!p || !p.url) return false;
      if (VIDEO_RE.test(p.filename || p.url || '')) return false;
      return STATIC_RE.test(p.url);
    });
    if (list.length === 0) {
      setBlobs({});
      return undefined;
    }
    var cancelled = false;
    var created = [];
    setBlobs({});

    var fetchOne = function(p) {
      var m = p.url.match(STATIC_RE);
      var thumbPath = '/eval/orders/' + m[1] + '/photos/' + m[2] + '/thumb';
      return authFetch(API + thumbPath, { cache: 'no-store' }).then(function(r) {
        if (!r || !r.ok) return null;
        return r.blob();
      }).then(function(blob) {
        if (!blob) return;
        if (cancelled) return;
        var blobUrl = URL.createObjectURL(blob);
        created.push(blobUrl);
        if (cancelled) {
          try { URL.revokeObjectURL(blobUrl); } catch (e) {}
          return;
        }
        setBlobs(function(prev) {
          var next = Object.assign({}, prev);
          next[p.url] = blobUrl;
          return next;
        });
      }).catch(function(err) {
        // 개별 fetch 실패는 해당 thumb 만 누락. silent catch 금지 룰 — 진단 1줄.
        console.error('useEvalThumbBlobs fetch failed for', p.url, err && err.message);
      });
    };

    // 50장 hard cap — photos 인풋 길이로 warn 트리거 (호출자 인지용), 실 fetch 는
    // filter 결과 list 에 cap 적용 (동영상/non-static URL 우회 방지).
    if (photos && photos.length > MAX_THUMB_LOAD) {
      console.warn('[useEvalThumbBlobs] photos exceed cap', photos.length, '/', MAX_THUMB_LOAD, '— 처음 50장만 로드. Wave 1D lazy load 필요.');
    }
    var capped = list.length > MAX_THUMB_LOAD ? list.slice(0, MAX_THUMB_LOAD) : list;
    var chunks = chunkArray(capped, CHUNK_SIZE);
    (async function() {
      for (var ci = 0; ci < chunks.length; ci++) {
        if (cancelled) break;
        await Promise.all(chunks[ci].map(fetchOne));
      }
    })();

    return function cleanup() {
      cancelled = true;
      created.forEach(function(u) { try { URL.revokeObjectURL(u); } catch (e) {} });
    };
  }, [sig]);  // eslint-disable-line react-hooks/exhaustive-deps

  return blobs;
}

export default useEvalThumbBlobs;
export { useEvalThumbBlobs };
